/**
 * Persian language utilities for text processing and validation
 */

/**
 * Persian character ranges and patterns
 */
export const PERSIAN_PATTERNS = {
  // Main Persian/Farsi characters
  persian: /[\u0600-\u06FF]/g,
  
  // Extended Arabic characters used in Persian
  arabicExtended: /[\u0750-\u077F]/g,
  
  // Arabic Presentation Forms
  arabicPresentationA: /[\uFB50-\uFDFF]/g,
  arabicPresentationB: /[\uFE70-\uFEFF]/g,
  
  // Persian digits
  persianDigits: /[۰-۹]/g,
  
  // Arabic digits
  arabicDigits: /[٠-٩]/g,
  
  // English digits
  englishDigits: /[0-9]/g,
  
  // Persian punctuation
  persianPunctuation: /[؟؍؎؏ؘؙؚؐؑؒؓؔؕؖؗ؛؜؝؞؟]/g,
  
  // Combined Persian text pattern
  allPersian: /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/g,
};

/**
 * Check if text contains Persian characters
 */
export const containsPersian = (text: string): boolean => {
  return PERSIAN_PATTERNS.allPersian.test(text);
};

/**
 * Check if text is primarily Persian
 */
export const isPrimaryPersian = (text: string): boolean => {
  if (!text || text.trim().length === 0) {
    return false;
  }

  const persianMatches = text.match(PERSIAN_PATTERNS.allPersian) || [];
  const totalChars = text.replace(/\s/g, '').length;
  
  return persianMatches.length / totalChars > 0.5;
};

/**
 * Validate Persian text input
 */
export const validatePersianText = (text: string): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!text || text.trim().length === 0) {
    errors.push('Text cannot be empty');
    return { isValid: false, errors };
  }

  // Check for invalid characters (basic validation)
  const invalidChars = text.match(/[^\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\s\d\p{P}]/gu);
  if (invalidChars && invalidChars.length > 0) {
    errors.push(`Invalid characters found: ${[...new Set(invalidChars)].join(', ')}`);
  }

  // Check minimum Persian content
  if (!containsPersian(text)) {
    errors.push('Text must contain Persian characters');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Clean and normalize Persian text
 */
export const normalizePersianText = (text: string): string => {
  if (!text) return '';

  return text
    // Normalize Arabic Yeh to Persian Yeh
    .replace(/ي/g, 'ی')
    // Normalize Arabic Kaf to Persian Kaf
    .replace(/ك/g, 'ک')
    // Normalize Zero Width Non-Joiner
    .replace(/\u200C+/g, '\u200C')
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Convert English digits to Persian digits
 */
export const toPersianDigits = (text: string): string => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  
  return text.replace(/[0-9]/g, (digit) => {
    return persianDigits[parseInt(digit, 10)];
  });
};

/**
 * Convert Persian digits to English digits
 */
export const toEnglishDigits = (text: string): string => {
  const persianToEnglish: Record<string, string> = {
    '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
    '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
    // Arabic-Indic digits
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
    '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
  };

  return text.replace(/[۰-۹٠-٩]/g, (digit) => {
    return persianToEnglish[digit] ?? digit;
  });
};

/**
 * Get text direction based on content
 */
export const getTextDirection = (text: string): 'rtl' | 'ltr' | 'auto' => {
  if (!text || text.trim().length === 0) {
    return 'auto';
  }

  const persianChars = (text.match(PERSIAN_PATTERNS.allPersian) || []).length;
  const englishChars = (text.match(/[A-Za-z]/g) || []).length;

  if (persianChars > englishChars) {
    return 'rtl';
  } else if (englishChars > persianChars) {
    return 'ltr';
  }

  return 'auto';
};

/**
 * Apply Persian text styling to an element
 */
export const applyPersianStyling = (element: HTMLElement, text?: string): void => {
  const textToAnalyze = text || element.textContent || '';
  const direction = getTextDirection(textToAnalyze);

  element.style.fontFamily = 'var(--font-persian)';
  element.style.direction = direction;
  element.style.unicodeBidi = 'plaintext';
  
  if (direction === 'rtl') {
    element.style.textAlign = 'right';
  } else if (direction === 'ltr') {
    element.style.textAlign = 'left';
  }
};

/**
 * Format Persian text for display
 */
export const formatPersianDisplay = (text: string, options: {
  convertDigits?: boolean;
  normalize?: boolean;
  direction?: 'rtl' | 'ltr' | 'auto';
} = {}): {
  text: string;
  direction: 'rtl' | 'ltr' | 'auto';
  className: string;
} => {
  let processedText = text;

  if (options.normalize) {
    processedText = normalizePersianText(processedText);
  }

  if (options.convertDigits) {
    processedText = toPersianDigits(processedText);
  }

  const direction = options.direction || getTextDirection(processedText);
  const className = direction === 'rtl' ? 'persian-text' : 
                   direction === 'ltr' ? 'english-text' : 'bidi-text';

  return {
    text: processedText,
    direction,
    className,
  };
};

/**
 * Persian text search utilities
 */
export const searchPersianText = (text: string, query: string): boolean => {
  if (!text || !query) return false;

  const normalizedText = normalizePersianText(text.toLowerCase());
  const normalizedQuery = normalizePersianText(query.toLowerCase());

  return normalizedText.includes(normalizedQuery);
};

/**
 * Highlight Persian text matches
 */
export const highlightPersianText = (text: string, query: string, highlightClass = 'bg-yellow-200'): string => {
  if (!text || !query) return text;

  const normalizedQuery = normalizePersianText(query);
  const regex = new RegExp(`(${normalizedQuery})`, 'gi');

  return text.replace(regex, `<span class="${highlightClass}">$1</span>`);
};

/**
 * Persian text truncation with proper handling
 */
export const truncatePersianText = (text: string, maxLength: number, suffix = '...'): string => {
  if (!text || text.length <= maxLength) {
    return text;
  }

  // Find a good breaking point (space or Persian word boundary)
  let truncateAt = maxLength;
  for (let i = maxLength; i >= Math.max(0, maxLength - 10); i--) {
    if (text[i] === ' ' || text[i] === '\u200C') { // Space or ZWNJ
      truncateAt = i;
      break;
    }
  }

  return text.substring(0, truncateAt).trim() + suffix;
};

/**
 * Count Persian words
 */
export const countPersianWords = (text: string): number => {
  if (!text || text.trim().length === 0) {
    return 0;
  }

  // Split by spaces and ZWNJ, filter out empty strings
  const words = text.trim().split(/[\s\u200C]+/).filter(word => word.length > 0);
  return words.length;
};

/**
 * Persian text statistics
 */
export const getPersianTextStats = (text: string): {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  persianChars: number;
  englishChars: number;
  digits: number;
  direction: 'rtl' | 'ltr' | 'auto';
} => {
  const characters = text.length;
  const charactersNoSpaces = text.replace(/\s/g, '').length;
  const words = countPersianWords(text);
  const persianChars = (text.match(PERSIAN_PATTERNS.allPersian) || []).length;
  const englishChars = (text.match(/[A-Za-z]/g) || []).length;
  const digits = (text.match(/[0-9۰-۹٠-٩]/g) || []).length;
  const direction = getTextDirection(text);

  return {
    characters,
    charactersNoSpaces,
    words,
    persianChars,
    englishChars,
    digits,
    direction,
  };
};